// Admin payout batch — disburse eligible merchant payouts via MonCash. Shows
// the prefunded float balance + whether the rail is live or simulated, the
// queue of eligible payouts with a one-click batch, and full history.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Banknote, Wallet, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState } from '../../components/shared/primitives';
import { ADMIN_NAV } from '../_navs';
import { admin, type Payout } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700', held: 'bg-slate-100 text-slate-600',
  approved: 'bg-blue-50 text-blue-700', cancelled: 'bg-slate-100 text-slate-500',
};
const FILTERS = ['all', 'pending', 'paid', 'failed'];

export function AdminPayoutsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const floatQ = useQuery({ queryKey: ['admin', 'payout-float'], queryFn: admin.payoutFloat });
  const listQ = useQuery({ queryKey: ['admin', 'payouts'], queryFn: () => admin.payouts() });

  const rows = useMemo(() => {
    const list = listQ.data ?? [];
    return filter === 'all' ? list : list.filter((p) => p.status === filter);
  }, [listQ.data, filter]);

  const pending = useMemo(() => (listQ.data ?? []).filter((p) => p.status === 'pending'), [listQ.data]);
  const pendingTotal = pending.reduce((a, p) => a + p.amount_htg_minor, 0);

  // Float guardrail: the prefunded balance must cover the pending queue.
  const floatHtgMinor = floatQ.data?.balance_htg != null ? Math.round(floatQ.data.balance_htg * 100) : null;
  const cantCover = floatHtgMinor != null && pendingTotal > 0 && floatHtgMinor < pendingTotal;
  const lowBuffer = floatHtgMinor != null && !cantCover && pendingTotal > 0 && floatHtgMinor < pendingTotal * 1.5;

  const batch = useMutation({
    mutationFn: () => admin.payoutBatch(),
    onSuccess: (res) => {
      toast.success(`${res.paid} paid · ${res.failed} failed (${res.mode})`);
      qc.invalidateQueries({ queryKey: ['admin', 'payouts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'payout-float'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Batch failed'),
  });

  return (
    <PortalShell nav={ADMIN_NAV} activeId="payouts" title="Payouts" subtitle="Disburse eligible merchant payouts to MonCash.">
      <div className="space-y-4">
        {cantCover && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-red-800">
              <strong>Float can't cover the pending queue.</strong> Prefunded balance {floatHtgMinor != null ? formatHtgMinor(floatHtgMinor) : '—'} &lt; eligible {formatHtgMinor(pendingTotal)}. Top up the Digicel prefunded account before disbursing, or only some payouts will go through.
            </p>
          </div>
        )}
        {lowBuffer && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-amber-800"><strong>Low float buffer.</strong> The balance covers the queue but with little headroom — schedule a top-up soon.</p>
          </div>
        )}

        {/* Float + batch */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card padding="p-3.5">
            <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Prefunded float</p>
            <p className="mt-1 text-[19px] font-bold text-slate-900">{floatQ.data?.balance_htg != null ? formatHtgMinor(Math.round(floatQ.data.balance_htg * 100)) : '—'}</p>
            <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${floatQ.data?.live ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {floatQ.data?.live ? 'MonCash live' : 'Simulated'}
            </span>
          </Card>
          <Card padding="p-3.5">
            <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">Eligible now</p>
            <p className="mt-1 text-[19px] font-bold text-amber-600">{formatHtgMinor(pendingTotal)}</p>
            <p className="text-[11px] text-slate-400">{pending.length} payout{pending.length === 1 ? '' : 's'} pending</p>
          </Card>
          <Card padding="p-3.5" className="flex flex-col justify-center">
            <button
              onClick={() => { if (window.confirm(`Disburse ${pending.length} payout(s) — ${formatHtgMinor(pendingTotal)} — via MonCash?`)) batch.mutate(); }}
              disabled={batch.isPending || pending.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-slate-900 text-white text-[13px] font-semibold hover:bg-slate-800 disabled:opacity-50"
            >
              {batch.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Pay all eligible
            </button>
            {floatQ.data && !floatQ.data.live && <p className="mt-1.5 text-[10.5px] text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Simulated until Digicel credentials are set</p>}
          </Card>
        </div>

        {/* Filter */}
        <Card padding="p-3" className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-[12px] font-semibold capitalize ${filter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{f}</button>
          ))}
        </Card>

        {/* History */}
        <Card padding="p-0">
          {listQ.isLoading ? (
            <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Banknote} title="No payouts" body="Payouts appear when merchants' deliveries are confirmed." />
          ) : (
            <table className="text-[12.5px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-5 font-medium">Merchant</th>
                  <th className="text-left py-2.5 px-3 font-medium">Order</th>
                  <th className="text-left py-2.5 px-3 font-medium">Method</th>
                  <th className="text-left py-2.5 px-3 font-medium">Status</th>
                  <th className="text-left py-2.5 px-3 font-medium">MonCash ref</th>
                  <th className="text-right py-2.5 px-5 font-medium">HTG</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => <Row key={p.id} p={p} />)}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function Row({ p }: { p: Payout }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <span className="font-semibold text-slate-900">{p.merchant?.business_name ?? `#${p.merchant_id}`}</span>
        {p.merchant?.merchant_code && <p className="text-[10.5px] font-mono text-slate-400">{p.merchant.merchant_code}</p>}
      </td>
      <td className="py-3 px-3 font-mono text-cyan-700">{p.request_id ? `REQ-${String(p.request_id).padStart(4, '0')}` : '—'}</td>
      <td className="py-3 px-3 text-slate-600 capitalize">{p.method.replace('_', ' ')}</td>
      <td className="py-3 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_TONE[p.status] ?? 'bg-slate-100 text-slate-600'}`}>{p.status}</span>{p.status === 'failed' && p.moncash_message && <p className="text-[10px] text-red-500 mt-0.5">{p.moncash_message}</p>}</td>
      <td className="py-3 px-3 font-mono text-[11px] text-slate-500">{p.moncash_transaction_id ?? p.moncash_reference ?? '—'}</td>
      <td className="py-3 px-5 text-right font-mono text-slate-900">{formatHtgMinor(p.amount_htg_minor, { withSymbol: false })}</td>
    </tr>
  );
}
